import { AccountExistError, ApiError, InvalidCredentialsError } from '@packages/shared/core/common/errors';
import { CookiesService, CrossDomainStorage, HttpClient } from '@packages/shared/core/common/services';
import { YEAR_IN_DAYS } from '@packages/shared/core/common/constants';
import { ApiPathAskGateway, ApiPathMain } from '@packages/shared/core/common/constants/apiPaths';
import { isBefore } from '@packages/shared/core/common/utils/date';
import { todayInMilliseconds } from '@packages/shared/core/common/utils/todayInMilliseconds';
import { userAuthenticatedSessionDtoToEntity } from '../mappers';
import { SignInData } from '../entities';
import type { AuthServiceDependencies } from '../entities/AuthServiceDependencies';
import { REFRESH_TOKEN_EXPIRE_AT_COOKIE_KEY } from '../constants';
import {
  CheckIfEmailExistsDto,
  NewUserDto,
  UserAuthenticatedSessionDto,
  RequestPasswordResetDto,
  ValidateEmailDto,
} from './dto';

export class AuthClientService {
  private readonly httpClient: HttpClient;

  private cookiesService: CookiesService;

  private crossDomainStorage: CrossDomainStorage;

  private askGatewayHttpClient: HttpClient;

  constructor({ httpClient, cookiesService, crossDomainStorage, askGatewayHttpClient }: AuthServiceDependencies) {
    this.httpClient = httpClient;
    this.cookiesService = cookiesService;
    this.crossDomainStorage = crossDomainStorage;
    this.askGatewayHttpClient = askGatewayHttpClient;
  }

  hasValidSession(): boolean {
    const expiresAt = this.cookiesService.get(REFRESH_TOKEN_EXPIRE_AT_COOKIE_KEY);

    return expiresAt != null && isBefore(todayInMilliseconds(), Number(expiresAt));
  }

  async getAuthenticationToken(): Promise<string> {
    const { data } = await this.askGatewayHttpClient.get<{ token: string }>(ApiPathAskGateway.GENERATE_AUTH_TOKEN);
    return data.token;
  }

  async signIn(signInData: SignInData) {
    try {
      const { data } = await this.httpClient.post<UserAuthenticatedSessionDto>(ApiPathMain.SIGN_IN, {
        data: signInData,
      });

      this.crossDomainStorage.setNebulaId(data.user.nebula_id);

      this.storeTokenExpiration(data.refresh_expires_at);

      return userAuthenticatedSessionDtoToEntity(data);
    } catch (err) {
      if (!(err instanceof ApiError)) {
        throw err;
      }

      if (err.message === 'Invalid credentials') {
        throw new InvalidCredentialsError(err);
      }

      throw err;
    }
  }

  async requestPasswordReset(email: string) {
    await this.askGatewayHttpClient.post<RequestPasswordResetDto>(ApiPathAskGateway.REQUEST_RESET_PASSWORD, {
      data: { email },
    });
  }

  async signUpViaQuiz(userInfo: NewUserDto) {
    try {
      const { data } = await this.httpClient.post<UserAuthenticatedSessionDto>(ApiPathMain.SIGN_UP_VIA_QUIZ, {
        data: userInfo,
      });
      this.crossDomainStorage.setNebulaId(data.user.nebula_id);
      this.storeTokenExpiration(data.refresh_expires_at);
      return data;
    } catch (err) {
      if (err instanceof ApiError) {
        this.throwApiError(err);
        return;
      }
      throw err;
    }
  }

  async isValidEmail(email: string) {
    const { data } = await this.askGatewayHttpClient.post<ValidateEmailDto>(ApiPathAskGateway.EMAIL_VALIDATE, {
      data: { email },
    });
    return data;
  }

  async doesEmailExists(email: string) {
    const { data } = await this.askGatewayHttpClient.get<CheckIfEmailExistsDto>(ApiPathAskGateway.ACCOUNT_EXISTS, {
      params: {
        email,
      },
    });
    return data;
  }

  async signUpViaExternal(userInfo: Omit<NewUserDto, 'email'>) {
    const { data } = await this.httpClient.post<UserAuthenticatedSessionDto>(ApiPathMain.SIGN_UP_VIA_EXTERNAL, {
      data: userInfo,
    });
    this.crossDomainStorage.setNebulaId(data.user.nebula_id);
    this.storeTokenExpiration(data.refresh_expires_at);
    return data;
  }

  private throwApiError(err: ApiError) {
    const error = err.message === 'Account exists' ? new AccountExistError(err) : err;
    throw error;
  }

  private storeTokenExpiration(expiredAt: number) {
    this.cookiesService.set(REFRESH_TOKEN_EXPIRE_AT_COOKIE_KEY, expiredAt.toString(), {
      expires: YEAR_IN_DAYS,
    });
  }
}
